/**
 * Helper functions of the wallet
 */
import { IBalanceResponse, Network } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import { Connector } from 'wagmi';
import { MetaMaskConnector } from 'wagmi/connectors/metaMask';
import { GetLastConnectedWallet } from '../components/ConnectedWallet/wallets.ls';
import { NATIVE_LINK } from './hooks/useLoadWalletData';
import {
  defaultWalletState,
  IWalletState,
  WalletTokenBalancesByAddr,
  WalletTokenBalancesBySym,
  WalletTokenRatesByAddr,
  WalletTokenRatesBySym,
  WalletTokensByAddr,
  WalletTokensBySym,
} from './walletReducer';
import { toChecksumAddressWrapper } from '../helpers/checksumAddress';

/**
 * Get connector for last connected wallet
 */
export const getConnector = (connectors: Connector[]) => {
  const lastWallet = GetLastConnectedWallet();
  const sorted = [...connectors].sort((x) => (x.id === lastWallet ? -1 : 1));

  let activeConnector: any = new MetaMaskConnector();
  for (const connector of sorted) {
    // eslint-disable-next-line curly
    if (!connector.ready) continue;

    activeConnector = connector;
    break;
  }

  return activeConnector;
};

/**
 * Get network flags for EVM, TRON and Elrond network types
 */
export const getNetworkFlags = (chainId?: number) => {
  if (!chainId) {
    return { isEVM: false, isElrond: false, isTron: false, isTon: false };
  }
  const network = Network.fromChainId(chainId);
  const isElrond = !!network?.isELROND();
  const isTron = !!network?.isTron();
  const isTon = !!network?.isTON();
  const isSolana = !!network?.isSOLANA();
  const isEVM = network ? !isElrond && !isTron && !isTon && !isSolana : false;

  return { isEVM, isElrond, isTron, isTon };
};

/**
 * Parse fetched data to wallet state
 */
export const computeWalletDerivativeState = (
  state: IWalletState,
  coins: IBalanceResponse[]
): IWalletState => {
  const tokenBalancesBySym: WalletTokenBalancesBySym = {};
  const tokenRatesBySym: WalletTokenRatesBySym = {};
  const tokensByAddr: WalletTokensByAddr = {};
  const tokensBySym: WalletTokensBySym = {};
  const tokenBalancesByAddr: WalletTokenBalancesByAddr = {};
  const tokenRatesByAddr: WalletTokenRatesByAddr = {};
  if (Array.isArray(coins)) {
    coins.forEach((coin: any) => {
      // Covalent
      const sym = coin.tokenInfo.contract_ticker_symbol;
      tokenBalancesBySym[sym] = coin.balance;
      tokenRatesBySym[sym] = coin.tokenInfo.quote_rate;
      tokensByAddr[coin.tokenInfo.contract_address] = {
        name: sym,
        fullName: coin.tokenInfo.contract_name,
        contract: coin.tokenInfo.contract_address,
        decimals: coin.tokenInfo.contract_decimals,
        balance: coin.balance,
        icon: coin.tokenInfo.logo_url,
        isNative: !coin.tokenInfo.supports_erc,
      };
      tokensBySym[sym] = {
        name: sym,
        fullName: coin.tokenInfo.contract_name,
        contract: coin.tokenInfo.contract_address,
        decimals: coin.tokenInfo.contract_decimals,
        balance: coin.balance,
        icon: coin.tokenInfo.logo_url,
        isNative: !coin.tokenInfo.supports_erc,
      };
      /**
       * New api
       */
      const validAddress = toChecksumAddressWrapper(
        coin.tokenInfo.contract_address
      );
      tokenBalancesByAddr[validAddress] = coin.balance;
      tokenRatesByAddr[validAddress] = coin.tokenInfo.quote_rate;
    });

    return {
      ...state,
      loading: false,
      error: false,
      tokenRatesBySym,
      tokenBalancesBySym,
      tokenBalancesByAddr,
      tokenRatesByAddr,
      tokensByAddr,
      tokensBySym,
    };
  } else {
    return defaultWalletState;
  }
};

/**
 * Update token balances for wallet
 */
export const joinWalletBalances = (
  state: IWalletState,
  balances: Record<string, string>
): IWalletState => {
  const mapping = Object.fromEntries(
    Object.entries(state.tokensByAddr).map(([k, v]) => [k.toLowerCase(), v])
  );

  const tknsWithJoinedBalances = Object.entries(balances).map(
    ([address, balance]) => {
      let symbol;
      if (address === NATIVE_LINK) {
        symbol = Object.values(mapping).find((el) => el.isNative)!;
      } else {
        symbol = mapping[address.toLowerCase()];
      }
      try {
        const rawBalance = BigNumber(balance);
        const denom = BigNumber(10).pow(symbol.decimals);
        symbol.balance = rawBalance.dividedBy(denom).toNumber();
      } catch (error) {
        console.warn(error);
      }
      return symbol;
    }
  );

  const tokenBalancesByAddr = { ...state.tokenBalancesByAddr };
  const tokensByAddr = { ...state.tokensByAddr };
  const tokensBySym = { ...state.tokensBySym };
  const tokenBalancesBySym = { ...state.tokenBalancesBySym };

  tknsWithJoinedBalances.forEach((tkn) => {
    if (tkn) {
      tokenBalancesBySym[tkn.name] = tkn.balance;
      tokensByAddr[tkn.contract].balance = tkn.balance;
      tokensBySym[tkn.name].balance = tkn.balance;
      tokenBalancesByAddr[tkn.contract] = tkn.balance;
    }
  });

  return {
    ...state,
    loading: false,
    error: false,
    tokenBalancesByAddr,
    tokenBalancesBySym,
    tokensByAddr,
    tokensBySym,
  };
};
